<!--
  ZoneTable part — the <tbody>: the loading skeleton, the empty state, and the rows themselves with
  their selection checkbox, nesting chevron and cells. INTERNAL: not a Zone component, no story,
  not in llms.txt. Rendered only by Table.vue.

  Presentational: it renders the rows it is handed and asks for changes through `row-click` /
  `toggle-row` / `toggle-expanded` / `empty-action`. The row pipeline that produced `rows` — the
  filtering, sorting, expansion flattening and page slice — stays in Table.vue.

  Class rules come from `./cells`, shared with Head.vue so a <td> and the <th> above it cannot
  disagree about width, alignment or sticky layer. Every <tr> paints an opaque background, which
  the pinned cells' `bg-inherit` depends on.

  The `#cell-KEY`, `#empty`, `#empty-image` and `#empty-actions` slots are relayed from Table.vue rather than
  re-declared — a dynamic slot name cannot be declared twice, only forwarded.
-->

<template>
  <tbody ref="tbody">
    <template v-if="isLoading">
      <!-- Opaque for the same reason as the header row: these cells are `bg-inherit`, and a
           pinned column can only hide what scrolls under it if it has a colour to hide it with. -->
      <tr
        v-for="skeletonIndex in skeletonCount"
        :key="`skeleton-${skeletonIndex}`"
        class="bg-white"
        :style="skeletonRowStyle"
        data-testid="skeleton-row"
      >
        <td
          v-if="selectable"
          :class="selectionClasses"
        >
          <div class="h-4 w-4 animate-pulse rounded bg-gray-200" />
        </td>
        <td
          v-for="column in columns"
          :key="column.key"
          :class="cellClasses(column)"
        >
          <div class="h-3 w-full animate-pulse rounded-full bg-gray-200" />
        </td>
      </tr>
    </template>

    <template v-else-if="!rows.length">
      <tr
        class="bg-white"
        data-testid="empty-state"
      >
        <td
          :colspan="columnCount"
          class="border-t border-gray-200 px-3 py-5 text-center"
        >
          <slot name="empty">
            <div class="flex flex-col items-center justify-center gap-3">
              <slot name="empty-image">
                <img
                  v-if="emptyImage"
                  :src="emptyImage"
                  alt=""
                  class="h-[94px] w-[98px] object-contain"
                />
                <!--
                  Zone's empty-table illustration, inlined from
                  app/assets/images/zone/empty-datatable.svg. It lives in the Sprockets
                  asset tree, which Vite cannot resolve from here, and it is small enough
                  that inlining beats teaching the build about a second asset root.
                -->
                <svg
                  v-else
                  width="86"
                  height="74"
                  viewBox="0 0 86 74"
                  fill="none"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M10.1172 26.8707C4.30827 36.6668 1.28457 49.4475 7.23739 59.0977C12.7889 68.0972 25.5844 66.4422 35.9661 68.7745C45.9807 71.0244 56.0645 77.0575 65.069 72.09C75.3057 66.4426 81.0907 55.0229 81.8773 43.4465C82.7135 31.1403 79.3593 17.7695 69.2426 10.6656C59.6721 3.94515 47.0756 7.93458 35.7327 11.0435C25.5428 13.8363 15.4842 17.8198 10.1172 26.8707Z"
                    fill="#F1F7FF"
                  />
                  <g filter="url(#zone-table-empty-shadow)">
                    <path
                      d="M10.6602 21.481H75.5754V52.6409C75.5754 53.9863 74.4848 55.0769 73.1395 55.0769H13.0961C11.7508 55.0769 10.6602 53.9863 10.6602 52.6409V21.481Z"
                      fill="#B8C9E0"
                    />
                    <path
                      d="M10.4938 9.67188L4.15926 23.6076C3.91848 24.1104 3.95248 24.7016 4.24932 25.1736C4.54615 25.6455 5.06433 25.9322 5.62185 25.9329H80.3764C80.9461 25.9329 81.4742 25.6341 81.7678 25.1458C82.0614 24.6575 82.0776 24.051 81.8105 23.5477L75.607 9.67188H10.4938Z"
                      fill="#E4EEFC"
                    />
                    <path
                      d="M62.4909 0L75.6065 9.67177H10.4883L23.5339 0H62.4909Z"
                      fill="#CDDBEE"
                    />
                    <path
                      d="M23.5339 0V9.67177H10.4883L23.5339 0Z"
                      fill="#B8C9E0"
                    />
                    <path
                      d="M62.4922 0V9.66467L75.6078 9.67177L62.4922 0Z"
                      fill="#B8C9E0"
                    />
                  </g>
                  <defs>
                    <filter
                      id="zone-table-empty-shadow"
                      x="0"
                      y="0"
                      width="86"
                      height="63.0767"
                      filterUnits="userSpaceOnUse"
                      color-interpolation-filters="sRGB"
                    >
                      <feFlood
                        flood-opacity="0"
                        result="BackgroundImageFix"
                      />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="4" />
                      <feGaussianBlur stdDeviation="2" />
                      <feComposite
                        in2="hardAlpha"
                        operator="out"
                      />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>
              </slot>
              <h3 class="text-lg font-medium text-gray-900">{{ emptyTitle }}</h3>
              <p
                v-if="emptyDescription"
                class="text-sm text-gray-600"
              >
                {{ emptyDescription }}
              </p>
              <slot name="empty-actions">
                <ZoneButton
                  v-if="emptyActionLabel"
                  size="sm"
                  color="primary_alt"
                  data-testid="empty-action"
                  @click="$emit('empty-action')"
                >
                  {{ emptyActionLabel }}
                </ZoneButton>
              </slot>
            </div>
          </slot>
        </td>
      </tr>
    </template>

    <template v-else>
      <tr
        v-for="entry in rows"
        :key="entry.key"
        :class="rowClasses(entry)"
        :data-level="entry.level"
        :data-expanded="entry.expanded ? 'true' : undefined"
        data-testid="table-row"
        @click="$emit('row-click', entry.row)"
      >
        <td
          v-if="selectable"
          :class="selectionClasses"
          @click.stop
        >
          <ZoneCheckbox
            data-testid="row-select"
            :model-value="isSelected(entry.key)"
            :disabled="!canSelectRow(entry.row)"
            @update:model-value="$emit('toggle-row', entry.key, $event)"
          />
        </td>
        <td
          v-for="(column, columnIndex) in columns"
          :key="column.key"
          :class="cellClasses(column, columnIndex, entry)"
        >
          <div class="flex min-w-0 items-center gap-1">
            <template v-if="columnIndex === 0 && hasNestedRows">
              <span
                v-if="entry.level"
                class="shrink-0"
                :style="{ width: `${entry.level * 16}px` }"
              />
              <button
                v-if="entry.hasChildren"
                type="button"
                class="inline-flex size-[18px] shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                :aria-expanded="entry.expanded ? 'true' : 'false'"
                :aria-label="entry.expanded ? t('collapse_row') : t('expand_row')"
                data-testid="row-toggle"
                @click.stop="$emit('toggle-expanded', entry.key)"
              >
                <ZoneIcon :icon="entry.expanded ? 'expand_more' : 'chevron_right'" />
              </button>
              <span
                v-else
                class="size-[18px] shrink-0"
              />
            </template>

            <div
              class="min-w-0 flex-1"
              :class="cellContentClasses(column)"
              :data-testid="`cell-${column.key}`"
            >
              <slot
                :name="`cell-${column.key}`"
                :row="entry.row"
                :value="cellValue(column, entry.row)"
                :column="column"
                :index="entry.index"
                :level="entry.level"
                :expanded="entry.expanded"
                :toggle="() => $emit('toggle-expanded', entry.key)"
              >
                <component
                  :is="rawComponent(column)"
                  v-if="column.component"
                  :row="entry.row"
                  :value="cellValue(column, entry.row)"
                  :column="column"
                />
                <span
                  v-else
                  class="block truncate"
                >{{ formattedValue(column, entry.row) }}</span>
              </slot>
            </div>
          </div>
        </td>
      </tr>
    </template>
  </tbody>
</template>

<script setup lang="ts">
import { computed, ref, toRaw, watch, type Component, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneIcon from '../Icon.vue';
import ZoneButton from '../Button.vue';
import ZoneCheckbox from '../Checkbox.vue';
import {
  bodyCellClasses,
  cellContentClasses as cellContentClassesFor,
  rowClasses as rowClassesFor,
  selectionCellClasses,
  type StickyOptions,
} from './cells';
import { formattedValue as formatValue } from './query';
import type { FlatRow, RowClassContext, TableColumn, TableRow, TableRowKey } from './types';

defineOptions({ name: 'ZoneTableBody' });

const { t } = useI18n();

const props = defineProps({
  columns: {
    type: Array as PropType<TableColumn[]>,
    required: true,
  },
  /** Already filtered, sorted, flattened and paged by Table.vue. */
  rows: {
    type: Array as PropType<FlatRow[]>,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  /** How many placeholder rows a first load draws; a reload draws one per row in state. */
  skeletonRows: {
    type: Number,
    default: 10,
  },
  /** Cells in one row, INCLUDING the checkbox column — the empty state's colspan. */
  columnCount: {
    type: Number,
    required: true,
  },
  selectable: {
    type: Boolean,
    required: true,
  },
  /** Whether any row in the tree has children, i.e. whether to reserve the chevron gutter. */
  hasNestedRows: {
    type: Boolean,
    required: true,
  },
  stickyOptions: {
    type: Object as PropType<StickyOptions>,
    required: true,
  },
  rowClass: {
    type: Function as PropType<((row: TableRow, context: RowClassContext) => unknown) | null>,
    default: null,
  },
  /** Predicates, passed as functions so this part needs no view of the selection itself. */
  isSelected: {
    type: Function as PropType<(key: TableRowKey) => boolean>,
    required: true,
  },
  canSelectRow: {
    type: Function as PropType<(row: TableRow) => boolean>,
    required: true,
  },
  emptyTitle: {
    type: String,
    default: null,
  },
  emptyDescription: {
    type: String,
    default: null,
  },
  emptyActionLabel: {
    type: String,
    default: null,
  },
  emptyImage: {
    type: String,
    default: null,
  },
});

defineEmits(['row-click', 'toggle-row', 'toggle-expanded', 'empty-action']);

// The skeleton stands in for the rows still in state while the next page loads: as many rows as
// they are, each as tall as they were, so the table keeps its height. Their height is read off the
// tbody the moment loading starts, before the DOM swaps them out (a `pre` watcher). A load with
// no rows in state (a first load, or after an empty state) draws `skeletonRows` placeholder rows at
// the default row height, so the skeleton is never flat.
const tbody = ref<HTMLTableSectionElement | null>(null);
const rowHeight = ref(0);

const DEFAULT_ROW_HEIGHT = 48;

watch(() => props.isLoading, (loading) => {
  if (loading) {
    if (props.rows.length) {
      rowHeight.value = (tbody.value?.getBoundingClientRect().height ?? 0) / props.rows.length;
    } else {
      rowHeight.value = DEFAULT_ROW_HEIGHT;
    }
  }
});

const skeletonCount = computed(() => props.rows.length || props.skeletonRows);
const skeletonRowStyle = computed(() => (rowHeight.value ? { height: `${rowHeight.value}px` } : undefined));

const selectionClasses = computed<string>(() => selectionCellClasses(props.stickyOptions, 'body'));

function cellClasses(column: TableColumn, columnIndex = 0, entry: FlatRow | null = null): string {
  return bodyCellClasses(column, props.stickyOptions, columnIndex, entry);
}

function cellContentClasses(column: TableColumn): string {
  return cellContentClassesFor(column);
}

function rowClasses(entry: FlatRow): unknown[] {
  return rowClassesFor(entry, props.isSelected(entry.key), props.rowClass);
}

function cellValue(column: TableColumn, row: TableRow): unknown {
  return row[column.key];
}

// `columns` is a reactive prop, so `column.component` comes back as a reactive proxy of the
// component definition — which Vue warns about (and needlessly deep-tracks) when handed to
// <component :is>. Unwrapping here means callers don't have to remember markRaw().
function rawComponent(column: TableColumn): Component | undefined {
  return column.component ? (toRaw(column.component) as Component) : undefined;
}

function formattedValue(column: TableColumn, row: TableRow): string {
  return formatValue(column, row);
}
</script>

<i18n lang="json">
{
  "en": {
    "collapse_row": "Collapse row",
    "expand_row": "Expand row"
  }
}
</i18n>
